import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { UseFormReturn } from 'react-hook-form'
import { trackInternalEvent } from '@rio/tracking'
import { useNavigate } from 'react-router-dom'
import { useUser } from 'builder/hooks/useUser'
import { useI18n } from 'builder/hooks/useI18n'
import { useConfig } from 'builder/hooks/useConfig'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { ConfigScopesEnum } from 'builder/modules/init'
import { actions } from 'builder/modules/panel'
import { DocumentTypes } from 'builder/modules/constants'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { selectors } from 'builder/modules/autoApply/autoApplyModule'
import { InfoBox } from 'builder/components/InfoBox'
import * as SimpleForm from 'builder/components/SimpleForm'
import { useDocument } from 'builder/hooks/useDocument'
import AutoTailorIcon from '../../assets/AutoTailorIcon.png'
import { getHost } from '../../utils'
import {
  AA_TAILOR_RESUME_CHECK_TEMP_KEY,
  StartCompleteScreenRoute,
  TAILOR_RESUME_KEY,
} from '../../constants'
import { usePreviewDocuments } from '../../hooks/usePreviewDocuments'
import { TailoringToggleCard } from '../TailoringToggleCard'
import { ResumePreview } from '../ResumePreview/ResumePreview'
import { ErrorText, InfoAction, InfoText } from './styles'

const TRANSLATION = 'builder.auto_apply.form.tailor_resume'
const TAILORING_CARD = `${TRANSLATION}.tailoring_resume_toggle_card`
type Props = {
  form: UseFormReturn<Pick<AutoApplyForm, 'autoTailoring'>>
}

export const TailorResumeForm = ({ form }: Props) => {
  const formState = useSelector(selectors.form)
  const { register, watch, setValue } = form

  const errorRef = useRef<HTMLDivElement>(null)
  const user = useUser()
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const config = useConfig(ConfigScopesEnum.country)
  const { features } = useFeaturesConfig()

  const { navigateToDocumentPreview, navigateToTrDocumentPreview } = usePreviewDocuments()
  const autoTailorEnabled = watch('autoTailoring')
  const trDocument = useDocument(
    formState.resumeType === DocumentTypes.trDocument ? formState.resumeId : null,
  )
  const trNewFlow = features.aaTailoring === 'v1'

  const onResumeSelect = () => {
    trackInternalEvent('aa_click_change_resume', { label: 'total_job_search_solution' })
    navigate(`/auto-apply/${StartCompleteScreenRoute.RESUME_SELECT}`)
  }
  const onSwitchToggle = (value: boolean) => {
    localStorage.setItem(AA_TAILOR_RESUME_CHECK_TEMP_KEY, value.toString())
    setValue('autoTailoring', value)
    setTimeout(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const onCreateResume = () => {
    // Key to use on JobSearchSolutionComponent and submit the resume to auto-apply
    localStorage.setItem(TAILOR_RESUME_KEY, 'true')
    trackInternalEvent('click_new_resume', {
      label: 'total_job_search_solution',
    })
    dispatch(actions.createDocument({ type: DocumentTypes.resume }))
  }

  const onPreviewDocument = () => {
    const { resumeId, resumeType } = formState
    if (!resumeId || !resumeType) return

    trackInternalEvent('open_full_screen_resume_preview', { label: 'auto_apply' })
    const isTrDocument = resumeType === DocumentTypes.trDocument

    if (isTrDocument) {
      if (!trNewFlow && trDocument?.resume_id && trDocument?.id) {
        if (autoTailorEnabled) {
          navigateToDocumentPreview({ id: trDocument?.resume_id })
        } else {
          navigateToTrDocumentPreview({ id: trDocument?.id, isTailoringEnabled: autoTailorEnabled })
        }
      } else {
        navigateToTrDocumentPreview({ id: resumeId, isTailoringEnabled: autoTailorEnabled })
      }
    } else {
      navigateToDocumentPreview({ id: resumeId })
    }
  }

  useEffect(() => {
    if (!!formState.id && !formState.resumeId && autoTailorEnabled) {
      form.setError('autoTailoring', {
        type: 'required',
        message: i18n.t(`${TRANSLATION}.upload_file_error`, {
          host: getHost(user?.scopes.kind, config?.name),
        }),
      })
    } else if (
      !!formState.id &&
      !!formState.resumeId &&
      !formState?.workExperiencesAttributes?.length &&
      autoTailorEnabled
    ) {
      form.setError('autoTailoring', {
        type: 'required',
        message: i18n.t(`${TRANSLATION}.job_experince_error`),
      })
    } else {
      form.clearErrors('autoTailoring')
    }
  }, [
    config?.name,
    form,
    formState.id,
    autoTailorEnabled,
    formState.resumeId,
    i18n,
    user?.scopes.kind,
    formState?.workExperiencesAttributes?.length,
  ])

  useEffect(() => {
    if (formState.id && !formState?.workExperiencesAttributes?.length && watch('autoTailoring')) {
      trackInternalEvent('missing_info_resume')
    }
  }, [])

  return (
    <>
      <ResumePreview
        resumeUrl={formState.resumeUrl}
        upladedAt={formState.resumeUploadedAt}
        selectedResumeId={formState.resumeId}
        selectedResumeType={formState.resumeType}
        onClickPreview={onPreviewDocument}
        handleBuilderResumeChange={onResumeSelect}
      />

      {!!formState.id && !formState.resumeId && (
        <SimpleForm.Row position="relative">
          <InfoBox
            type="info"
            text={
              <InfoText>
                {i18n.t(`${TRANSLATION}.info.first`, {
                  host: getHost(user?.scopes.kind, config?.name),
                })}{' '}
                <InfoAction onClick={onCreateResume}>
                  {i18n.t(`${TRANSLATION}.info.second`)}
                </InfoAction>{' '}
                {i18n.t(`${TRANSLATION}.info.third`)}
              </InfoText>
            }
          />
        </SimpleForm.Row>
      )}
      <SimpleForm.Row position="relative">
        <TailoringToggleCard
          {...register('autoTailoring')}
          checked={watch('autoTailoring')}
          onToggle={onSwitchToggle}
          image={<img height="98" width="130" src={AutoTailorIcon} />}
          titleText={i18n.t(`${TAILORING_CARD}.title`)}
          subtitleText={i18n.t(`${TAILORING_CARD}.subtitle${trNewFlow ? '_2' : ''}`)}
          toggleText={i18n.t(`${TRANSLATION}.tailoring_resume_toggle_card.toggle_text`)}
        />

        {form.formState.errors.autoTailoring?.message && (
          <ErrorText ref={errorRef}>{form.formState.errors.autoTailoring?.message}</ErrorText>
        )}
      </SimpleForm.Row>
    </>
  )
}
